import type { KlassenDaten } from "@core/asd/data/klassen/KlassenDaten";
import type { KursDaten } from "@core/asd/data/kurse/KursDaten";
import type { SchuelerLeistungsdaten } from "@core/asd/data/schueler/SchuelerLeistungsdaten";
import type { SchuelerLernabschnittsdaten } from "@core/asd/data/schueler/SchuelerLernabschnittsdaten";
import type { Schuljahresabschnitt } from "@core/asd/data/schule/Schuljahresabschnitt";
import { Fach } from "@core/asd/types/fach/Fach";
import { Jahrgaenge } from "@core/asd/types/jahrgang/Jahrgaenge";
import { Note } from "@core/asd/types/Note";
import type { Schulform } from "@core/asd/types/schule/Schulform";
import { Schulgliederung } from "@core/asd/types/schule/Schulgliederung";
import type { FachDaten } from "@core/core/data/fach/FachDaten";
import type { JahrgangsDaten } from "@core/core/data/jahrgang/JahrgangsDaten";
import type { LehrerListeEintrag } from "@core/core/data/lehrer/LehrerListeEintrag";
import type { SchuelerListeEintrag } from "@core/core/data/schueler/SchuelerListeEintrag";
import type { FoerderschwerpunktEintrag } from "@core/core/data/schule/FoerderschwerpunktEintrag";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { KlassenUtils } from "@core/core/utils/klassen/KlassenUtils";
import { KursUtils } from "@core/core/utils/kurse/KursUtils";
import { JavaLong } from "@core/java/lang/JavaLong";
import { JavaString } from "@core/java/lang/JavaString";
import { ArrayList } from "@core/java/util/ArrayList";
import type { Comparator } from "@core/java/util/Comparator";
import { HashMap } from "@core/java/util/HashMap";
import { HashSet } from "@core/java/util/HashSet";
import type { JavaMap } from "@core/java/util/JavaMap";
import type { JavaSet } from "@core/java/util/JavaSet";
import type { List } from "@core/java/util/List";
import { useFaecherState } from "@ui/states/kataloge/FaecherState";
import { useFoerderschwerpunkteState } from "@ui/states/kataloge/FoerderschwerpunkteState";
import { FoerderschwerpunkteListeManager } from "@ui/ui/manager/kataloge/FoerderschwerpunkteListeManager";

/**
 * Ein Manager zum Verwalten der Schüler-Lernabschnittsdaten eines Schülers.
 */
export class SchuelerLernabschnittManager {

	// Die Schulform der Schule
	private readonly _schulform: Schulform;

	// Die Informationen zum aktuell ausgewählten Schüler
	private readonly _schueler: SchuelerListeEintrag;

	// Die Lernabschnittsdaten des aktuell ausgewählten Lernabschnitts. Diese beinhalten auch die zugehörigen Leistungsdaten
	private readonly _lernabschnittsdaten: SchuelerLernabschnittsdaten;

	// Die Informationen zum Schuljahresabschnitt auf welcgen sich die Lernabschnittsdaten beziehen
	private readonly _schuljahresabschnitt: Schuljahresabschnitt;

	// Eine Map für den schnellen Zugriff auf die Leistungsdaten des Schülers anhand der ID der Leistungsdaten
	private readonly _mapLeistungById: JavaMap<number, SchuelerLeistungsdaten> = new HashMap<number, SchuelerLeistungsdaten>();

	// Der Katalog der Jahrgänge
	private readonly _jahrgaenge: List<JahrgangsDaten> = new ArrayList<JahrgangsDaten>();

	// Eine Map für den schnellen Zugriff auf die Jahrgänge anhand ihrer ID
	private readonly _mapJahrgangByID: JavaMap<number, JahrgangsDaten> = new HashMap<number, JahrgangsDaten>();

	// Der Katalog der Klassen in dem Schuljahresabschnitt
	private readonly _klassen: List<KlassenDaten> = new ArrayList<KlassenDaten>();

	// Eine Map für den schnellen Zugriff auf die Klassen anhand ihrer ID
	private readonly _mapKlasseByID: JavaMap<number, KlassenDaten> = new HashMap<number, KlassenDaten>();

	// Der Katalog der Kurse in dem Schuljahresabschnitt
	private readonly _kurse: List<KursDaten> = new ArrayList<KursDaten>();

	// Eine Map für den schnellen Zugriff auf die Kurse anhand ihrer ID
	private readonly _mapKursByID: JavaMap<number, KursDaten> = new HashMap<number, KursDaten>();

	// Der Katalog der Lehrer
	private readonly _lehrer: List<LehrerListeEintrag> = new ArrayList<LehrerListeEintrag>();

	// Der Katalog der Lehrer, gefiltert anhand des Zugangs- und des Abgangsdatum des Lehrers
	private readonly _lehrerAktiv: JavaSet<LehrerListeEintrag> = new HashSet<LehrerListeEintrag>();

	// Der Katalog der sichbaren Lehrer, zusätzlich gefiltert anhand des Zugangs- und des Abgangsdatum des Lehrers
	private readonly _lehrerAktivUndSichtbar: JavaSet<LehrerListeEintrag> = new HashSet<LehrerListeEintrag>();

	// Eine Map für den schnellen Zugriff auf die Lehrer anhand ihrer ID
	private readonly _mapLehrerByID: JavaMap<number, LehrerListeEintrag> = new HashMap<number, LehrerListeEintrag>();

	private readonly faecherState = useFaecherState();

	private readonly foerderschwerpunkteState = useFoerderschwerpunkteState();

	// Comparator: Führt einen Vergleich von zwei Fächern durch
	private static readonly _compFach: Comparator<FachDaten> = { compare: (a: FachDaten, b: FachDaten) => {
		let cmp: number = a.sortierung - b.sortierung;
		if (cmp !== 0) {
			return cmp;
		}
		cmp = JavaString.compareTo(a.kuerzel, b.kuerzel);
		return (cmp === 0) ? JavaLong.compare(a.id, b.id) : cmp;
	} };

	// Comparator: Führt einen Vergleich von zwei Lehrern durch
	private static readonly _compLehrer: Comparator<LehrerListeEintrag> = { compare: (a: LehrerListeEintrag, b: LehrerListeEintrag) => {
		let cmp: number = JavaString.compareTo(a.nachname, b.nachname);
		if (cmp !== 0) {
			return cmp;
		}
		cmp = JavaString.compareTo(a.vorname, b.vorname);
		if (cmp !== 0) {
			return cmp;
		}
		cmp = a.sortierung - b.sortierung;
		return (cmp === 0) ? JavaLong.compare(a.id, b.id) : cmp;
	} };

	// Comparator: Führt einen Vergleich von zwei Leistungsdatensätzen anhand des Faches durch
	private readonly _compLeistungenByFach: Comparator<SchuelerLeistungsdaten> = { compare: (a: SchuelerLeistungsdaten, b: SchuelerLeistungsdaten) => {
		const aFach: FachDaten | undefined = this.faecherState.faecher.byId.get(a.fachID);
		const bFach: FachDaten | undefined = this.faecherState.faecher.byId.get(b.fachID);
		if (!aFach || !bFach) {
			throw new DeveloperNotificationException("Fach nicht gefunden.");
		}
		return SchuelerLernabschnittManager._compFach.compare(aFach, bFach);
	} };

	// Comparator: Führt einen Vergleich von zwei Jahrgängen durch
	private static readonly jahrgangsDatencomparator: Comparator<JahrgangsDaten> = { compare: (a: JahrgangsDaten, b: JahrgangsDaten) => {
		let cmp: number = a.sortierung - b.sortierung;
		if (cmp !== 0) {
			return cmp;
		}
		if ((a.kuerzel !== null) && (b.kuerzel !== null)) {
			cmp = JavaString.compareTo(a.kuerzel, b.kuerzel);
			if (cmp !== 0) {
				return cmp;
			}
		}
		return JavaLong.compare(a.id, b.id);
	} };


	/**
	 * Erstellt einen neuen Manager mit den übergebenen Lernabschnittsdaten und den übergebenen Katalogen
	 *
	 * @param schulform             die Schulform der Schule des Schülers
	 * @param schueler              Informationen zu dem Schüler
	 * @param lernabschnittsdaten   die Lernabschnittsdaten
	 * @param schuljahresabschnitt  der Schuljahresabschnitt der Lernabschnittsdaten
	 * @param jahrgaenge            der Katalog der Jahrgänge
	 * @param klassen               der Katalog der Klassen
	 * @param kurse                 der Katalog der Kurse
	 * @param lehrer                der Katalog der Lehrer
	 */
	public constructor(
		schulform: Schulform,
		schueler: SchuelerListeEintrag,
		lernabschnittsdaten: SchuelerLernabschnittsdaten,
		schuljahresabschnitt: Schuljahresabschnitt,
		jahrgaenge: List<JahrgangsDaten>,
		klassen: List<KlassenDaten>,
		kurse: List<KursDaten>,
		lehrer: List<LehrerListeEintrag>
	) {
		this._schulform = schulform;
		this._schueler = schueler;
		this._lernabschnittsdaten = lernabschnittsdaten;
		this._schuljahresabschnitt = schuljahresabschnitt;
		this.initLeistungsdaten(lernabschnittsdaten.leistungsdaten);
		this.initJahrgaenge(jahrgaenge);
		this.initKlassen(klassen);
		this.initKurse(kurse);
		this.initLehrer(lehrer);
	}

	/**
	 * Initialisiert die internen Datenstrukturen für die Leistungsdaten
	 *
	 * @param leistungsdaten   die Leistungsdaten aus dem Schüler-Lernabschnitt
	 */
	private initLeistungsdaten(leistungsdaten: List<SchuelerLeistungsdaten>): void {
		for (const leistung of leistungsdaten) {
			this.leistungAddInternal(leistung);
		}
	}

	/**
	 * Initialisiert die internen Datenstrukturen für die Jahrgänge und führt dabei
	 * eine Sortierung der internen Liste aus.
	 *
	 * @param jahrgaenge   die Liste der Jahrgänge für die Initialisierung
	 */
	private initJahrgaenge(jahrgaenge: List<JahrgangsDaten>): void {
		this._jahrgaenge.clear();
		this._jahrgaenge.addAll(jahrgaenge);
		this._jahrgaenge.sort(SchuelerLernabschnittManager.jahrgangsDatencomparator);
		this._mapJahrgangByID.clear();
		for (const j of jahrgaenge) {
			this._mapJahrgangByID.put(j.id, j);
		}
	}

	/**
	 * Initialisiert die internen Datenstrukturen für die Klassen und führt dabei
	 * eine Sortierung der internen Liste aus.
	 *
	 * @param klassen   die Liste der Klassen für die Initialisierung
	 */
	private initKlassen(klassen: List<KlassenDaten>): void {
		this._klassen.clear();
		this._klassen.addAll(klassen);
		this._klassen.sort(KlassenUtils.comparator);
		this._mapKlasseByID.clear();
		for (const k of klassen) {
			this._mapKlasseByID.put(k.id, k);
		}
	}

	/**
	 * Initialisiert die internen Datenstrukturen für die Kurse und führt dabei
	 * eine Sortierung der internen Liste aus.
	 *
	 * @param kurse   die Liste der Kurse für die Initialisierung
	 */
	private initKurse(kurse: List<KursDaten>): void {
		this._kurse.clear();
		this._kurse.addAll(kurse);
		this._kurse.sort(KursUtils.comparator);
		this._mapKursByID.clear();
		for (const k of kurse) {
			this._mapKursByID.put(k.id, k);
		}
	}

	/**
	 * Initialisiert die internen Datenstrukturen für die Lehrer und führt dabei
	 * eine Sortierung der internen Liste aus.
	 *
	 * @param lehrer   die Liste der Lehrer für die Initialisierung
	 */
	private initLehrer(lehrer: List<LehrerListeEintrag>): void {
		this._lehrer.clear();
		this._lehrer.addAll(lehrer);
		this._lehrer.sort(SchuelerLernabschnittManager._compLehrer);
		this._mapLehrerByID.clear();
		for (const l of lehrer) {
			this._mapLehrerByID.put(l.id, l);
			if (l.istAktiv) {
				this._lehrerAktiv.add(l);
				if (l.istSichtbar) {
					this._lehrerAktivUndSichtbar.add(l);
				}
			}
		}
	}


	/**
	 * Gibt die Lernabschnittsdaten dieses Managers zurück.
	 *
	 * @return die Lernabschnittsdaten
	 */
	public lernabschnittGet(): SchuelerLernabschnittsdaten {
		return this._lernabschnittsdaten;
	}

	/**
	 * Gibt die Schulgliederung zurück, die dem Lernabschnitt zugeordnet ist oder
	 * null, falls keine Zuordnung existiert.
	 *
	 * @return die Schulgliederung oder null
	 */
	public lernabschnittGetGliederung(): Schulgliederung | null {
		if (this._lernabschnittsdaten.idSchulgliederung === null) {
			return null;
		}
		return Schulgliederung.data().getWertByIDOrNull(this._lernabschnittsdaten.idSchulgliederung);
	}

	/**
	 * Gibt den Statistik-Jahrgang zurück, der dem Lernabschnitt zugeordnet ist oder null,
	 * falls kein Jahrgang zugeordnet ist.
	 *
	 * @return der Statistik-Jahrgang
	 */
	public lernabschnittGetStatistikJahrgang(): Jahrgaenge | null {
		if (this._lernabschnittsdaten.jahrgangID === null) {
			return null;
		}
		const eintrag: JahrgangsDaten | null = this._mapJahrgangByID.get(this._lernabschnittsdaten.jahrgangID);
		if ((eintrag === null) || (eintrag.idJahrgang === null)) {
			return null;
		}
		return Jahrgaenge.data().getWertByIDOrNull(eintrag.idJahrgang);
	}

	/**
	 * Gibt die Bezeichnung für die Lernbereichtsnote 1 zurück, sofern eine angegeben werden kann.
	 *
	 * @return die Bezeichnung für die Lernbereichtsnote 1
	 */
	public lernabschnittGetLernbereichsnote1Bezeichnung(): string | null {
		const jg: Jahrgaenge | null = this.lernabschnittGetStatistikJahrgang();
		if (jg === null) {
			return null;
		}
		return jg.getLernbereichsnote1Bezeichnung(this._schulform, this.lernabschnittGetGliederung(), this._schuljahresabschnitt.schuljahr);
	}

	/**
	 * Gibt die Bezeichnung für die Lernbereichtsnote 2 zurück, sofern eine angegeben werden kann.
	 *
	 * @return die Bezeichnung für die Lernbereichtsnote 2
	 */
	public lernabschnittGetLernbereichsnote2Bezeichnung(): string | null {
		const jg: Jahrgaenge | null = this.lernabschnittGetStatistikJahrgang();
		if (jg === null) {
			return null;
		}
		return jg.getLernbereichsnote2Bezeichnung(this._schulform, this.lernabschnittGetGliederung(), this._schuljahresabschnitt.schuljahr);
	}


	/**
	 * Fügt einen Leistungsdatensatz in die interne Map ein.
	 *
	 * @param leistungsdaten   der hinzuzufügende Leistungsdatensatz
	 */
	private leistungAddInternal(leistungsdaten: SchuelerLeistungsdaten): void {
		this._mapLeistungById.put(leistungsdaten.id, leistungsdaten);
	}

	/**
	 * Fügt die übergebenen Leistungsdaten zu dem Lernabschnitt hinzu
	 *
	 * @param leistungsdaten   die hinzuzufügenden Leistungsdaten
	 */
	public leistungAdd(leistungsdaten: SchuelerLeistungsdaten): void {
		this._lernabschnittsdaten.leistungsdaten.add(leistungsdaten);
		this.leistungAddInternal(leistungsdaten);
	}

	/**
	 * Entfernt die übergebenen Leistungsdaten anhand der ID aus dem Lernabschnitt
	 *
	 * @param idLeistungsdaten   die ID der zu entfernenden Leistungsdaten
	 */
	public leistungRemoveByID(idLeistungsdaten: number): void {
		for (let i: number = this._lernabschnittsdaten.leistungsdaten.size() - 1; i >= 0; i--) {
			const leistung: SchuelerLeistungsdaten = this._lernabschnittsdaten.leistungsdaten.get(i);
			if (leistung.id === idLeistungsdaten) {
				this._lernabschnittsdaten.leistungsdaten.remove(leistung);
			}
		}
		this._mapLeistungById.remove(idLeistungsdaten);
	}

	/**
	 * Gibt die Leistungsdaten für die übergebene ID zurück.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Leistungsdaten
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public leistungGetByIdOrException(idLeistung: number): SchuelerLeistungsdaten {
		return DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
	}

	/**
	 * Gibt die Menge der Leistungsdaten sortiert anhand des Faches zurück.
	 *
	 * @return die Menge der Leistungsdaten
	 */
	public leistungGetMengeAsListSortedByFach(): List<SchuelerLeistungsdaten> {
		const result: List<SchuelerLeistungsdaten> = new ArrayList<SchuelerLeistungsdaten>();
		result.addAll(this._lernabschnittsdaten.leistungsdaten);
		result.sort(this._compLeistungenByFach);
		return result;
	}

	/**
	 * Prüft, ob ein Kurs mit den Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return true, falls ein Kurs mit den Leistungsdaten verknüpft ist
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public leistungHatKurs(idLeistung: number): boolean {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return this._mapKursByID.get(leistung.kursID) !== null;
	}

	/**
	 * Prüft, ob ein Lehrer mit den Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return true, falls ein Lehrer mit den Leistungsdaten verknüpft ist
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public leistungHatLehrer(idLeistung: number): boolean {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return this._mapLehrerByID.get(leistung.lehrerID) !== null;
	}

	/**
	 * Ermittelt die Informationen zum Fach, welche mit den Leistungsdaten verknüpft sind.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Fach-Informationen oder null, wenn kein Fach zugeordnet ist
	 */
	public fachGetByLeistungId(idLeistung: number): FachDaten | null {
		const leistung: SchuelerLeistungsdaten | null = this._mapLeistungById.get(idLeistung);
		if (leistung === null) {
			return null;
		}
		return this.faecherState.faecher.byId.get(leistung.fachID) ?? null;
	}

	/**
	 * Ermittelt die Informationen zu der Fach-Farbe, welche den Leistungsdaten zugeordnet ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Farbe und falls kein Fach zugeordnet ist oder die ID der Leistungsdaten nicht korrekt ist, die Default-Farbe rgb(220,220,220)
	 */
	public fachFarbeGetByLeistungsIdOrDefault(idLeistung: number): string {
		const fachDaten: FachDaten | null = this.fachGetByLeistungId(idLeistung);
		if (fachDaten === null) {
			return "rgb(220,220,220)";
		}
		return Fach.getBySchluesselOrDefault(fachDaten.kuerzelStatistik).getHMTLFarbeRGB(this._schuljahresabschnitt.schuljahr);
	}

	/**
	 * Ermittelt die Informationen zu dem Förderschwerpunkt mit der angegebenen ID.
	 *
	 * @param id   die ID des Förderschwerpunktes
	 *
	 * @return die Förderschwerpunkt-Informationen
	 * @throws DeveloperNotificationException falls kein Förderschwerpunkt mit der ID existiert
	 */
	public foerderschwerpunktGetByIdOrException(id: number): FoerderschwerpunktEintrag {
		const foerderschwerpunkt = this.foerderschwerpunkteState.foerderschwerpunkte.byId.get(id);
		if (foerderschwerpunkt === undefined) {
			throw new DeveloperNotificationException("Es existiert kein Förderschwerpunkt mit der ID " + id + ".");
		}
		return foerderschwerpunkt;
	}

	/**
	 * Gibt die sortierte Liste der Förderschwerpunkte aus dem Katalog-State zurück.
	 *
	 * @return die sortierte Liste der Förderschwerpunkte
	 */
	public foerderschwerpunktGetMenge(): List<FoerderschwerpunktEintrag> {
		const liste = new ArrayList<FoerderschwerpunktEintrag>(this.foerderschwerpunkteState.foerderschwerpunkte.list);
		liste.sort(FoerderschwerpunkteListeManager.comparator);
		return liste;
	}


	/**
	 * Ermittelt die Informationen zu dem Jahrgang mit der angegebenen ID.
	 *
	 * @param id   die ID des Jahrgangs
	 *
	 * @return die Jahrgangs-Informationen oder null, falls die ID ungültig ist
	 */
	public jahrgangGetByIdOrNull(id: number): JahrgangsDaten | null {
		return this._mapJahrgangByID.get(id);
	}

	/**
	 * Ermittelt die Informationen zu dem Jahrgang mit der angegebenen ID.
	 *
	 * @param id   die ID des Jahrgangs
	 *
	 * @return die Jahrgangs-Informationen
	 * @throws DeveloperNotificationException falls kein Jahrgang mit der ID existiert
	 */
	public jahrgangGetByIdOrException(id: number): JahrgangsDaten {
		return DeveloperNotificationException.ifMapGetIsNull(this._mapJahrgangByID, id);
	}

	/**
	 * Gibt die Liste der Jahrgänge zurück.
	 *
	 * @return die Liste der Jahrgänge
	 */
	public jahrgangGetMenge(): List<JahrgangsDaten> {
		return this._jahrgaenge;
	}


	/**
	 * Ermittelt die Informationen zu der Klasse mit der angegebenen ID.
	 *
	 * @param id   die ID der Klasse
	 *
	 * @return die Klassen-Informationen oder null, falls die ID ungültig ist
	 */
	public klasseGetByIdOrNull(id: number): KlassenDaten | null {
		return this._mapKlasseByID.get(id);
	}

	/**
	 * Ermittelt die Informationen zu der Klasse mit der angegebenen ID.
	 *
	 * @param id   die ID der Klasse
	 *
	 * @return die Klassen-Informationen
	 * @throws DeveloperNotificationException falls keine Klasse mit der ID existiert
	 */
	public klasseGetByIdOrException(id: number): KlassenDaten {
		return DeveloperNotificationException.ifMapGetIsNull(this._mapKlasseByID, id);
	}

	/**
	 * Gibt die Liste der Klassen zurück.
	 *
	 * @return die Liste der Klassen
	 */
	public klasseGetMenge(): List<KlassenDaten> {
		return this._klassen;
	}


	/**
	 * Ermittelt die Informationen zu dem Kurs mit der angegebenen ID.
	 *
	 * @param id   die ID des Kurses
	 *
	 * @return die Kurs-Informationen oder null, falls die ID nicht gültig ist
	 */
	public kursGetByIdOrNull(id: number): KursDaten | null {
		return this._mapKursByID.get(id);
	}

	/**
	 * Ermittelt die Informationen zu dem Kurs mit der angegebenen ID.
	 *
	 * @param id   die ID des Kurses
	 *
	 * @return die Kurs-Informationen
	 * @throws DeveloperNotificationException falls kein Kurs mit der ID existiert
	 */
	public kursGetByIdOrException(id: number): KursDaten {
		return DeveloperNotificationException.ifMapGetIsNull(this._mapKursByID, id);
	}

	/**
	 * Ermittelt die Informationen zu dem Kurs, sofern einer mit diesen Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Kurs-Informationen oder null, falls kein Kurs zugeordnet ist.
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public kursGetByLeistungIdOrNull(idLeistung: number): KursDaten | null {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return this._mapKursByID.get(leistung.kursID);
	}

	/**
	 * Ermittelt die Informationen zu dem Kurs, sofern einer mit diesen Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Kurs-Informationen
	 * @throws DeveloperNotificationException falls kein Kurs zugeordnet ist oder die ID der Leistungsdaten nicht korrekt ist
	 */
	public kursGetByLeistungIdOrException(idLeistung: number): KursDaten {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return DeveloperNotificationException.ifMapGetIsNull(this._mapKursByID, leistung.kursID);
	}

	/**
	 * Gibt die Liste der Kurse zurück.
	 *
	 * @return die Liste der Kurse
	 */
	public kursGetMenge(): List<KursDaten> {
		return this._kurse;
	}

	/**
	 * Gibt die Liste der Kurse zurück und filtert diese anhand des Jahrgangs des Schülers sowie
	 * des Faches der Leistungsdaten.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die gefilterte Liste der Kurse
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public kursGetMengeFilteredByLeistung(idLeistung: number): List<KursDaten> {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		const result: List<KursDaten> = new ArrayList<KursDaten>();
		for (const k of this._kurse) {
			if ((k.idFach === leistung.fachID) && (k.idJahrgaenge.isEmpty() || k.idJahrgaenge.contains(this._lernabschnittsdaten.jahrgangID))) {
				result.add(k);
			}
		}
		return result;
	}


	/**
	 * Ermittelt die Informationen zu dem Lehrer mit der angegebenen ID.
	 *
	 * @param id   die ID des Lehrers
	 *
	 * @return die Lehrer-Informationen
	 * @throws DeveloperNotificationException falls kein Lehrer mit der ID existiert
	 */
	public lehrerGetByIdOrException(id: number): LehrerListeEintrag {
		return DeveloperNotificationException.ifMapGetIsNull(this._mapLehrerByID, id);
	}

	/**
	 * Ermittelt die Informationen zu dem Lehrer, sofern einer mit diesen Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Lehrer-Informationen oder null, falls kein Lehrer zugeordnet ist.
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public lehrerGetByLeistungIdOrNull(idLeistung: number): LehrerListeEintrag | null {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return this._mapLehrerByID.get(leistung.lehrerID);
	}

	/**
	 * Ermittelt die Informationen zu dem Lehrer, sofern einer mit diesen Leistungsdaten verknüpft ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die Lehrer-Informationen
	 * @throws DeveloperNotificationException falls kein Lehrer zugeordnet ist oder die ID der Leistungsdaten nicht korrekt ist
	 */
	public lehrerGetByLeistungIdOrException(idLeistung: number): LehrerListeEintrag {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return DeveloperNotificationException.ifMapGetIsNull(this._mapLehrerByID, leistung.lehrerID);
	}

	/**
	 * Gibt die Liste der Lehrer zurück.
	 *
	 * @return die Liste der Lehrer
	 */
	public lehrerGetMenge(): List<LehrerListeEintrag> {
		return this._lehrer;
	}

	/**
	 * Gibt die Liste der aktiven Lehrer zurück.
	 *
	 * @return die Liste der aktiven Lehrer
	 */
	public lehrerGetMengeAktiv(): JavaSet<LehrerListeEintrag> {
		return this._lehrerAktiv;
	}

	/**
	 * Gibt die Liste der aktiven und sichtbaren Lehrer zurück.
	 *
	 * @return die Liste der aktiven und sichtbaren Lehrer
	 */
	public lehrerGetMengeAktivUndSichtbar(): JavaSet<LehrerListeEintrag> {
		return this._lehrerAktivUndSichtbar;
	}


	/**
	 * Ermittelt die Note, welche den Leistungsdaten zugewiesen ist.
	 *
	 * @param idLeistung   die ID der Leistungsdaten
	 *
	 * @return die zugewiesene Note - falls keine zugewiesen ist wird Note.KEINE oder eine Pseudonote zurückgegeben
	 * @throws DeveloperNotificationException falls die ID der Leistungsdaten nicht korrekt ist
	 */
	public noteGetByLeistungIdOrException(idLeistung: number): Note {
		const leistung: SchuelerLeistungsdaten = DeveloperNotificationException.ifMapGetIsNull(this._mapLeistungById, idLeistung);
		return Note.fromKuerzel(leistung.note);
	}


	/**
	 * Gibt die Schulform der Schule des Schülers zurück.
	 *
	 * @return die Schulform der Schule des Schülers
	 */
	public schulformGet(): Schulform {
		return this._schulform;
	}


	/**
	 * Gibt den Schuljahresabschnitt des Lernabschnittes zurück.
	 *
	 * @return der Schuljahresabschnitt des Lernabschnittes
	 */
	public schuljahresabschnittGet(): Schuljahresabschnitt {
		return this._schuljahresabschnitt;
	}


	/**
	 * Gibt das Schuljahr des Lernabschnittes zurück.
	 *
	 * @return das Schuljahr des Lernabschnittes
	 */
	public schuljahrGet(): number {
		return this._schuljahresabschnitt.schuljahr;
	}


	/**
	 * Gibt die Informationen des Schülers zurück, zu dem die Lernabschnittsdaten gehören.
	 *
	 * @return die Informationen des Schülers
	 */
	public schuelerGet(): SchuelerListeEintrag {
		return this._schueler;
	}

}
